import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LayoutConfig, User } from '../../types/config';
import { useTenantCurrency } from '../../context/TenantCurrencyContext';
import { useMediaDisplayUrl } from '../../hooks/useMediaDisplayUrl';
import { useSchoolDisplayName } from '../../hooks/useSchoolDisplayName';
import { roleLabel } from '../../utils/roles';
import { NotificationsBell } from './NotificationsBell';
import { ShellHeaderAccent } from './ShellHeaderAccent';

interface HeaderProps {
  config: LayoutConfig['header'];
  user?: Pick<User, 'name' | 'avatar' | 'role' | 'email'>;
  onSearch?: (query: string) => void;
  onNotificationClick?: () => void;
  onLogout?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  config,
  user,
  onSearch,
  onLogout,
}) => {
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isPlatformAdmin = user?.role === 'super_admin';
  const { tenant } = useTenantCurrency();
  const logoSrc = useMediaDisplayUrl(
    isPlatformAdmin ? null : tenant?.logo_media_id,
    isPlatformAdmin ? null : tenant?.logo_url,
    !isPlatformAdmin && tenant?.tenant_id && tenant.tenant_id > 0 ? tenant.tenant_id : undefined
  );
  const {
    displayTitle,
    hasMultipleBranches,
    branchOptions,
    selectedBranchId,
    selectBranch,
    loading,
  } = useSchoolDisplayName(config.title, { platformBranding: isPlatformAdmin });

  useEffect(() => {
    setLogoBroken(false);
  }, [logoSrc]);

  useEffect(() => {
    if (displayTitle) {
      document.title = displayTitle;
    }
  }, [displayTitle]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  if (!config.enabled) return null;

  const accentVariant = isPlatformAdmin ? 'platform' : 'default';

  return (
    <header className="relative overflow-visible border-b border-secondary-700/60 shadow-sm z-30">
      <ShellHeaderAccent variant={accentVariant} />
      <div className="relative z-[2] px-6 py-3.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button
              type="button"
              onClick={() => history.push('/')}
              className="relative hidden sm:flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5 cursor-pointer transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/40"
              aria-label="Go to dashboard"
              title="Dashboard"
            >
              {logoSrc && !logoBroken ? (
                <img
                  src={logoSrc}
                  alt=""
                  className="h-full w-full object-contain bg-white/90 p-0.5"
                  onError={() => setLogoBroken(true)}
                />
              ) : (
                <i className="fas fa-graduation-cap text-primary-300/90 text-lg" aria-hidden />
              )}
            </button>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.16em] text-secondary-300 font-medium mb-0.5">
                {isPlatformAdmin ? 'Platform Admin' : 'Institution Portal'}
              </p>
              <h1 className="font-display text-lg sm:text-xl font-semibold text-royal-ivory truncate">
                {loading && !displayTitle ? config.title : displayTitle}
              </h1>
            </div>
            {hasMultipleBranches && (
              <select
                className="text-sm border border-white/10 rounded-lg px-3 py-2 bg-secondary-800/70 text-secondary-100 max-w-[14rem] shrink-0 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400/50"
                value={selectedBranchId}
                onChange={(e) => selectBranch(e.target.value)}
                aria-label="Select branch"
              >
                {branchOptions.map((branch) => (
                  <option key={branch.id} value={branch.id} className="text-secondary-900">
                    {branch.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative hidden md:block">
              <input
                type="text"
                placeholder="Search…"
                className="royal-input pl-10 pr-4 py-2 w-52 text-sm text-secondary-900 placeholder:text-secondary-500"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  onSearch?.(e.target.value);
                }}
              />
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-primary-600 text-sm" />
            </div>
            {!isPlatformAdmin && <NotificationsBell />}
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
                onClick={() => setMenuOpen((o) => !o)}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full ring-1 ring-white/20" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary-500/80 flex items-center justify-center ring-1 ring-white/15">
                    <i className="fas fa-user text-secondary-900 text-sm" />
                  </div>
                )}
                <span className="text-sm font-medium text-secondary-100 hidden sm:inline">{user?.name}</span>
                <i className="fas fa-chevron-down text-secondary-300 text-xs hidden sm:inline" aria-hidden />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-52 rounded-lg border border-white/10 bg-secondary-800 shadow-md py-1 z-[100]"
                  role="menu"
                >
                  {user?.role && (
                    <p className="px-4 py-2 text-xs text-secondary-300 capitalize border-b border-white/5">
                      {roleLabel(user.role)}
                    </p>
                  )}
                  {user?.email ? (
                    <p className="px-4 py-2 text-xs text-secondary-400 truncate border-b border-white/5">
                      {user.email}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full text-left px-4 py-2.5 text-sm text-secondary-100 hover:bg-white/10 flex items-center gap-2"
                    onClick={() => {
                      setMenuOpen(false);
                      history.push('/profile');
                    }}
                  >
                    <i className="fas fa-user-circle text-secondary-300 w-4" aria-hidden />
                    My profile
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full text-left px-4 py-2.5 text-sm text-secondary-100 hover:bg-white/10 flex items-center gap-2 border-t border-white/5"
                    onClick={() => {
                      setMenuOpen(false);
                      onLogout?.();
                    }}
                  >
                    <i className="fas fa-right-from-bracket text-secondary-300 w-4" aria-hidden />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
